/**
 * `/` and `/about` — the way in, for everyone.
 *
 * The page is one plate of night with the real ring in it, and one column of
 * paper underneath. The ring is the argument: it is the actual `Scene` and
 * `InputController` over the sample year, so a stranger has used the product
 * before they have read a word about it. Everything below it explains, in a
 * manual's voice, what it does and what it costs.
 *
 * Copy lives here, in this file, as plain strings. There is no CMS and no
 * translation layer, and a landing page whose words are scattered across
 * components is a landing page nobody edits.
 */
import type { PageContext, Teardown } from '../router';
import {
  browserSupportsWebAuthn,
  githubEnabled,
  githubSignInUrl,
  invitesRequired,
  loadAppConfig,
  passkeyErrorMessage,
  rememberGithubSignIn,
  signInWithPasskey,
} from '../auth';
import { RingSeal } from '../../ui/ring-seal';
import { button, coarsePointer, el, setChildren, withBusy, type Child } from '../../ui/dom';
import { wireSegmented } from '../../ui/segmented';
import { toast } from '../../ui/toast';
import { mountDemoRing, prefersReducedMotion } from '../../ui/landing/demo-ring';
import { sampleViews } from '../../data/sample';
import { mountInviteCreate, type InviteCreateHandle } from '../../ui/invite-create';
import {
  calendarSections,
  loadCalendars,
  noCalendars,
  type CalendarsState,
} from '../../ui/landing/calendars';
import { outputsPlate, ringPlate, sourcesPlate } from '../../ui/landing/figures';

export const REPO_URL = 'https://github.com/silolis/circles';

export async function renderLandingPage(ctx: PageContext): Promise<Teardown> {
  const today = new Date();
  let calendars: CalendarsState = noCalendars;

  const ringBox = el('div', { class: 'hero__ring' });
  const modes = el('div', {
    class: 'segmented hero__modes',
    role: 'radiogroup',
    'aria-label': 'How the circles are drawn',
  });
  const calendarSlot = el('div', { class: 'lp-slot' });
  const createSlot = el('div', { class: 'lp-create' });
  const createLede = el('p', { class: 'lp-lede' });
  const signInSlot = el('div', { class: 'lp-signin' });

  const root = el(
    'div',
    { class: 'landing' },
    hero(ringBox, modes, today),
    el(
      'div',
      { class: 'landing-paper' },
      el(
        'div',
        { class: 'landing-col' },
        calendarSlot,
        createSection(createLede, createSlot, signInSlot),
        howItWorks(today),
        whatItKeeps(),
        siteFooter(today, calendars),
      ),
    ),
  );
  ctx.host.appendChild(root);

  const demo = mountDemoRing(ringBox, { today });

  /**
   * Merged and Split, over the live ring.
   *
   * The one thing about circles a stranger has to see rather than read is that
   * a group can leave the ring and become a ring of its own — so the front page
   * lets them do it, on the sample year, with the same tween the app uses. Two
   * of the three modes, because two is the argument and three is a control
   * panel; the sample views are the same rows a calendar would have.
   */
  const DEMO_MODES = [
    { id: 'vw_everyone00000000001', label: 'Merged' },
    { id: 'vw_split0000000000001', label: 'Split' },
  ];
  let demoMode = DEMO_MODES[0]!.id;

  const paintModes = (): void => {
    setChildren(
      modes,
      DEMO_MODES.map((mode) =>
        el(
          'button',
          {
            class: 'segmented__option',
            type: 'button',
            role: 'radio',
            'data-mode': mode.label.toLowerCase(),
            'aria-checked': demoMode === mode.id ? 'true' : 'false',
            text: mode.label,
            onClick: () => {
              const view = sampleViews.find((v) => v.id === mode.id);
              if (!view || demoMode === mode.id) return;
              demoMode = mode.id;
              demo.setViewLayout(view.layout);
              paintModes();
            },
          },
        ),
      ),
    );
    // Two buttons with role=radio promise the radio pattern; `wireSegmented`
    // is what keeps that promise — one tab stop, ←/→ moving the check.
    wireSegmented(modes);
  };
  paintModes();

  /** Redrawn whenever the config or the sign-in state lands. */
  const paint = (): void => {
    createLede.textContent = createLedeCopy(Boolean(calendars.user), invitesRequired());
    setChildren(signInSlot, [signIn(calendars, () => void refresh())]);
    setChildren(calendarSlot, calendarSections(calendars));
    const foot = root.querySelector('.lp-foot');
    if (foot) foot.replaceWith(siteFooter(today, calendars));
  };

  async function refresh(): Promise<void> {
    // Painted twice: once from what this device remembers, and again as each
    // key is checked against the server (ui/landing/calendars.ts).
    calendars = await loadCalendars((next) => {
      calendars = next;
      paint();
    });
    paint();
  }

  paint();

  void loadAppConfig().then(paint);
  void refresh();
  // The form asks for a code when this deployment wants one, keeps the owner
  // token, and shows the owner link itself. `onCreated` fires once that link
  // has been read and dismissed, so all that is left is to open the new ring.
  const invite: InviteCreateHandle = mountInviteCreate(createSlot, {
    onCreated: (calendar) => ctx.navigate(`/circles/${calendar.id}`),
  });

  // `/about` is this page with the manual already open.
  if (ctx.path.startsWith('/about')) scrollTo(root, '#how');

  return () => {
    demo.destroy();
    invite.destroy();
  };
}

function scrollTo(root: HTMLElement, selector: string): void {
  const go = (): void => {
    const target = root.querySelector(selector);
    if (!(target instanceof HTMLElement)) return;
    target.scrollIntoView({
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      block: 'start',
    });
  };
  if (typeof requestAnimationFrame === 'function') requestAnimationFrame(go);
  else go();
}

// --- the plate ---------------------------------------------------------------

/**
 * The line under the headline, and it has to name the gesture the reader
 * actually has: "scroll to zoom" on a phone is an instruction for a device
 * that is not in their hand.
 */
export function heroHintCopy(coarse: boolean): string {
  return coarse
    ? 'Drag to spin it, pinch to zoom in. This is the real ring, with a sample year in it.'
    : 'Drag to spin it, scroll to zoom in. This is the real ring, with a sample year in it.';
}

function hero(ringBox: HTMLElement, modes: HTMLElement, _today: Date): HTMLElement {
  return el(
    'header',
    { class: 'landing-hero' },
    el(
      'div',
      { class: 'hero__plate' },
      // No seal up here: the live ring two lines below is the mark.
      el('div', { class: 'hero__mark' }, el('span', { text: 'Circles' })),
      el('h1', {
        class: 'hero__line',
        text: 'A year is a ring. The people you keep are marked around it.',
      }),
      el('p', {
        class: 'hero__hint',
        text: heroHintCopy(coarsePointer()),
      }),
      el('p', {
        class: 'hero__hint',
        text: 'Family, work, whoever else — split them onto rings of their own, or fold them back into one.',
      }),
    ),
    el('div', { class: 'hero__stage' }, ringBox, modes),
    el('p', {
      class: 'sr-only',
      text: 'An interactive ring showing a sample year: twelve months clockwise from January at the top, with twenty-eight birthdays and notes marked around it. Two buttons switch between drawing every circle on one ring and giving each a ring of its own. Everything it can show you is written out below.',
    }),
  );
}

// --- paper -------------------------------------------------------------------

/**
 * The line above the create form, and it has to say what the form below it is
 * actually doing.
 *
 * The gate lets a signed-in person past without a code (`ui/invite-create`), so
 * the invitation copy was being read by people who were shown no code box —
 * asking for something the page had already decided not to want. Signing in is
 * the third state, not a variation of one of the other two.
 */
export function createLedeCopy(signedIn: boolean, gated: boolean): string {
  if (signedIn) {
    return 'Another ring, then. It takes a name and nothing else, and it joins the calendars on your account — with a link of its own that is the key to it, to keep or to hand on.';
  }
  if (gated) {
    return 'Circles is invitation-only for now, while it is small enough to keep an eye on. If someone gave you a code, this is where it goes.';
  }
  return 'It takes a name and nothing else. What you get back is a link that is the key to it — an account is optional, and can come later.';
}

function createSection(lede: HTMLElement, slot: HTMLElement, signInSlot: HTMLElement): HTMLElement {
  return el(
    'section',
    { class: 'lp-section', id: 'start' },
    el('h2', { text: 'Start your ring' }),
    lede,
    slot,
    signInSlot,
  );
}

/**
 * Coming back, rather than starting.
 *
 * It sits under the create form as one quiet line, not beside it: as a sibling
 * of the form it landed between the invitation-code box and the name box, where
 * it reads as a step in filling the form in rather than as the other way in.
 * Signing in is what a visitor does *instead* of everything above it.
 */
function signIn(calendars: CalendarsState, onSignedIn: () => void): Child {
  const line = (...children: Child[]): HTMLElement =>
    el('p', { class: 'small muted' }, ...children);

  const user = calendars.user;
  if (user) {
    return line(
      `Signed in as ${user.name}. `,
      el('a', { class: 'btn btn--link', href: '/settings', text: 'Your account' }),
      '.',
    );
  }
  if (!browserSupportsWebAuthn()) {
    return githubEnabled()
      ? line(
          'Already have a ring? ',
          el('a', {
            class: 'btn btn--link',
            href: githubSignInUrl('/'),
            rel: 'external',
            text: 'Sign in with GitHub',
            onClick: rememberGithubSignIn,
          }),
          '.',
        )
      : line(
          'This browser cannot make passkeys, so there is no way to sign in here. Calendars you hold by a link still work.',
        );
  }
  const btn = button(
    'Sign in',
    () =>
      void withBusy(btn, async () => {
        try {
          const user = await signInWithPasskey();
          toast(`Welcome back, ${user.name}.`);
          onSignedIn();
        } catch (err) {
          toast(passkeyErrorMessage(err), 'error');
        }
      }),
    { class: 'btn--link' },
  );
  return line('Already have a ring? ', btn, '.');
}

interface Step {
  title: string;
  body: string;
  plate: SVGSVGElement;
  caption: string;
}

function howItWorks(today: Date): HTMLElement {
  const year = today.getFullYear();
  const steps: Step[] = [
    {
      title: 'Sources in',
      body: 'Your birthdays are probably already written down somewhere. Paste the address of an ICS feed and Circles follows it, refreshing overnight: the feed owns the dates, you own the photos and the colours. Send a collect link to the people themselves and they fill in their own, for you to approve. Or type one in — that always works, and needs nobody else.',
      plate: sourcesPlate(year),
      caption:
        'Three sources landing on the days they belong to. Each keeps its own colour on the ring.',
    },
    {
      title: 'The ring',
      body: 'One year, January at twelve o’clock, going clockwise. A face sits outside the ring, level with its day, tied to it by a thin line. Zoom in and a crowded week spreads out; zoom out and it folds back into one card that names who is in it. Drag a face to where you want it and it stays there, for everyone you have shared the ring with.',
      plate: ringPlate(year),
      caption:
        'Four birthdays in the second week of March, folded into a single card until there is room for them.',
    },
    {
      title: 'Out again',
      body: 'Share a link and anyone who has it sees the ring, read-only, and can arrange their own copy without touching yours. Subscribe from your phone’s calendar and the birthdays turn up there every year, with the age already worked out and a reminder the morning before if you asked for one.',
      plate: outputsPlate(year),
      caption: 'One link to look at it, one feed to live in the calendar you already carry.',
    },
  ];

  return el(
    'section',
    { class: 'lp-section', id: 'how' },
    el('h2', { text: 'How it works' }),
    el('p', {
      class: 'lp-lede',
      text: 'Circles is a lens over calendars you already have. Three things happen, in this order.',
    }),
    ...steps.map((step) =>
      el(
        'article',
        { class: 'lp-step' },
        el('h3', { text: step.title }),
        el('p', { text: step.body }),
        el('figure', { class: 'lp-figure' }, step.plate, el('figcaption', { text: step.caption })),
      ),
    ),
  );
}

function whatItKeeps(): HTMLElement {
  const keys: [string, string][] = [
    ['The owner link', 'opens the calendar and can change everything in it. Yours alone. Keep it.'],
    ['The share link', 'shows the ring and nothing else. No adding, no editing, no deleting, and no sight of your other calendars.'],
    ['The collect link', 'opens a form with one name and one date on it. Whoever fills it in never sees the ring.'],
    ['The subscribe feed', 'is the same birthdays as an ICS file, for a calendar app to follow.'],
  ];

  return el(
    'section',
    { class: 'lp-section', id: 'privacy' },
    el('h2', { text: 'What it keeps, and what it costs' }),
    el('p', {
      text: 'Circles keeps names, dates and photos. That is the whole list. There are no ads, no trackers and nothing to install, and nothing is ever sold or shared with anyone.',
    }),
    el('p', {
      text: 'A link is a key. There is one for each thing you might want to hand out, they are separate on purpose, and any of them can be replaced the moment you want it to stop working.',
    }),
    el(
      'dl',
      { class: 'lp-keys' },
      ...keys.flatMap(([term, meaning]) => [
        el('dt', { text: term }),
        el('dd', { text: meaning }),
      ]),
    ),
    el('p', {
      text: 'Adding a passkey is optional. It attaches the calendar to you, so you can sign in from another device, switch the owner link off, and get back in if you lose it.',
    }),
    el('p', {
      text: 'It costs nothing and it is meant to keep costing nothing: Circles runs on free infrastructure with hard caps, which is also why feeds are limited, calendars are small, and a photo is cropped and shrunk to 512 pixels in your browser before it is ever uploaded.',
    }),
  );
}

function siteFooter(today: Date, calendars: CalendarsState): HTMLElement {
  // Big enough that the day graduation still reads as graduation.
  const seal = new RingSeal({ size: 68, year: today.getFullYear(), mark: null, label: false });
  return el(
    'footer',
    { class: 'lp-foot' },
    el('span', { class: 'lp-foot__mark' }, seal.root, el('span', { text: 'Made with Circles' })),
    el('nav', { class: 'lp-foot__links', 'aria-label': 'Footer' },
      el('a', { href: '/about', text: 'About' }),
      el('a', { href: REPO_URL, rel: 'external noopener', target: '_blank', text: 'Source on GitHub' }),
      calendars.user ? el('a', { href: '/settings', text: 'Settings' }) : null,
    ),
  );
}
